/**
 * How the cart screens are allowed to write.
 *
 * ── WHY THIS IS A MODULE AND NOT TWO LINES INSIDE A COMPONENT ────────────────
 * Both rules below are about ORDER and DUPLICATION — the two things that are invisible
 * in a screenshot, survive every rendering test, and show up as a footer that disagrees
 * with what the customer is charged. They are pure functions here so they can be tested
 * for the thing that actually breaks (a second call arriving before the first answers),
 * which the app's SSR test environment cannot provoke through a rendered button.
 *
 * Neither of these is a cache and neither holds a cart. The cart is server state: every
 * response still comes from the API and is handed to the screen unchanged. All these do
 * is decide WHEN a request is allowed to leave.
 */

/**
 * Run cart mutations one at a time, in the order they were asked for.
 *
 * ── THE BUG THIS EXISTS FOR ──────────────────────────────────────────────────
 * D3's stepper fires a write per tap. Fired in parallel, two quick taps on `+` send
 * `PATCH 3` and `PATCH 4` and there are two ways to end up wrong:
 *
 *   the RESPONSES race   `PATCH 4` answers first, then `PATCH 3` answers and its body is
 *                        applied last. The screen settles on ৩ and ৩'s total while the
 *                        server holds 4, and nothing corrects it — the screen only
 *                        re-reads the cart on mount.
 *   the REQUESTS race    `PATCH 4` reaches the server first. The server settles on 3 and
 *                        the screen shows 4. Same disagreement, opposite side.
 *
 * Serialising removes both at once: one write is in flight at a time, so arrival order is
 * issue order, and the last response applied is the last intent expressed.
 *
 * A rejection is swallowed on purpose and must be: the failure arm is the screen's
 * (a failed tap leaves the rest of the cart alone), and an escaping rejection would
 * strand every write queued behind it.
 */
export function createWriteQueue(): (run: () => Promise<unknown>) => void {
  let tail: Promise<unknown> = Promise.resolve();
  return (run: () => Promise<unknown>): void => {
    // Both arms run the next write: a tap that failed must not cancel the taps behind it.
    const settled = tail.then(
      () => run(),
      () => run(),
    );
    // The queue's own handle never rejects, so one failure cannot strand the rest.
    tail = settled.then(
      () => undefined,
      () => undefined,
    );
  };
}

/**
 * Let the first call through and drop the rest until it fails.
 *
 * For an ADD that is not idempotent and has no `Idempotency-Key` to lean on:
 * `POST /v1/cart/basket` puts the basket in the cart again on a second tap, and
 * `idempotent()` wraps exactly one route in the API — `POST /v1/orders`. Sending a key to
 * a route that does not read one would claim a guarantee the server has not made, so the
 * duplicate is refused where it starts.
 *
 * IT DOES NOT RE-OPEN ON SUCCESS, deliberately. H3's success navigates to the cart, and a
 * screen that is leaving must not accept another basket on the way out. It re-opens on
 * FAILURE, because a tap that did not land has to be retryable.
 */
export function createSingleFlight<A extends unknown[]>(
  run: (...args: A) => Promise<unknown>,
): (...args: A) => void {
  let busy = false;
  return (...args: A): void => {
    if (busy) return;
    busy = true;
    // `Promise.resolve().then` so a synchronous throw inside `run` reaches the same arm
    // as a rejected promise, rather than escaping and leaving the gate stuck shut.
    void Promise.resolve()
      .then(() => run(...args))
      .catch(() => {
        busy = false;
      });
  };
}
